import config from "../../config.json";
import { notifyError } from "../notify";
const BASE_AUTH_API = config.BASE_AUTH_API;

// Why a stores request failed (NA-033). Only used by `fetchStoresAPI(..., { strict: true })`.
export const STORES_ERROR = Object.freeze({
    NETWORK: "network", // no answer (offline, DNS, CORS)
    REJECTED: "rejected", // 401 / 403: the token is not accepted
    SERVER: "server", // 5xx
    INVALID: "invalid", // unusable answer
});

export class StoresError extends Error {
    constructor(kind, status) {
        super(`stores request failed: ${kind}`);
        this.name = "StoresError";
        this.kind = kind;
        this.status = status;
    }
}

const STORES_UNAVAILABLE = {
    en: "Could not load the restaurant list. Check your connection and try again.",
    ar: "تعذّر تحميل قائمة المطاعم. تحقق من اتصالك وحاول مرة أخرى.",
};

const STORES_WRITE_FAILED = {
    en: "The restaurant change could not be sent. Check your connection and try again.",
    ar: "تعذّر إرسال تعديل المطعم. تحقق من اتصالك وحاول مرة أخرى.",
};

/**
 * Loads the stores the backend allows this user to see.
 *
 * Default: resolves to the list, or to `undefined` on any failure (unchanged
 * contract). A network failure raises one non-blocking notice instead of a
 * browser `alert`.
 *
 * `{ strict: true }` (access checks): throws a `StoresError` that says why, and
 * shows nothing, so the caller decides how to report and when to retry.
 */
export const fetchStoresAPI = async (token, ids, { strict = false } = {}) => {
    const params = new URLSearchParams();
    (ids || []).forEach(id => params.append('id', id));
    const url = `${BASE_AUTH_API}stores?${params.toString()}`;

    let response;
    try {
        response = await fetch(url, {
            method: 'GET',
            headers: {
                Accept: 'application/form-data',
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
    } catch {
        if (strict) throw new StoresError(STORES_ERROR.NETWORK);
        notifyError(STORES_UNAVAILABLE, { key: "stores-unavailable" });
        return;
    }

    let responseData;
    try {
        responseData = await response.json();
    } catch {
        responseData = undefined;
    }

    if (responseData?.success && Array.isArray(responseData.stores)) {
        return responseData.stores;
    }
    if (strict) {
        const status = response.status;
        const kind =
            status === 401 || status === 403
                ? STORES_ERROR.REJECTED
                : status >= 500
                  ? STORES_ERROR.SERVER
                  : STORES_ERROR.INVALID;
        throw new StoresError(kind, status);
    }
    return;
}

export const updateStoreAPI = async (token, id, data) => {
    let responseData;
    try {
        await fetch(`${BASE_AUTH_API}stores/${id}`, {
            method: 'PUT',
            headers: {
                Accept: 'application/form-data',
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        }).then((response) => response.json()).then((data) => responseData = data);
        if (responseData.success) {
            return responseData.updated
        }
        else {
            return
        }
    } catch (error) {
        notifyError(STORES_WRITE_FAILED, { key: "stores-write-failed" })
        return
    }
}

export const deleteStoreAPI = async (token, id) => {
    let responseData;
    try {
        await fetch(`${BASE_AUTH_API}stores/${id}`, {
            method: 'DELETE',
            headers: {
                Accept: 'application/form-data',
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        }).then((response) => response.json()).then((data) => responseData = data);

        if (responseData.success) {
            console.log(responseData.message)
            return true
        } else {
            return
        }
    } catch (error) {
        notifyError(STORES_WRITE_FAILED, { key: "stores-write-failed" })
        return
    }
}

/**
 * Stored delivery-app credentials of ONE restaurant, from the dedicated endpoint (the list/detail
 * responses never carry them). Resolves to `{ storeId, userInfo, branches }` or `null` when the
 * caller may not read them (403/404) or the request failed - no notice is shown, the caller
 * decides how to report it.
 */
export const fetchStoreCredentialsAPI = async (token, storeCode) => {
    if (!token || !storeCode) return null;
    try {
        const response = await fetch(`${BASE_AUTH_API}stores/${encodeURIComponent(storeCode)}/credentials`, {
            method: 'GET',
            headers: {
                Accept: 'application/json',
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        const data = await response.json();
        return data?.success && data.credentials ? data.credentials : null;
    } catch (error) {
        return null;
    }
}

/**
 * An existing customer (store / storeL) adds another draft store to their OWN account. No user is created.
 * Resolves to { store, stores, between, replayed }; throws an Error carrying `status` and `code` (the form shows the message).
 * `body.requestId` is generated once per form: sending it again (retry, double click) returns the same store.
 */
export const createMyStoreAPI = async (token, body) => {
    const response = await fetch(`${BASE_AUTH_API}stores/mine`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
        const error = new Error(data.message || data.error || 'Request failed');
        error.status = response.status;
        error.code = data.code;
        throw error;
    }
    return data;
};
